import axios from 'utils/axios';
import type {
    DirProposalFullType,
    DocumentDirectoryType,
    DocumentEmailHistoryType,
    LeadCloudFileType,
    ObjectProposalGenerationCreateDataType,
    ObjectProposalGenerationType,
    ProposalSaveDataType,
} from '@econtractor/types';

export interface DocumentListParams {
    page?: number;
    search?: string;
    order?: string;
    onPage?: number;
    /** Filter by one document type; omit for all document templates. */
    proposalType?: DocumentDirectoryType;
}

interface GetDirDocumentListResultType {
    documents: any[];
    paginator: any;
}

export const getDirList = async (params: DocumentListParams): Promise<GetDirDocumentListResultType | null> => {
    try {
        const response = await axios('/document', { params });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getDirDocument = async (documentId: number | string): Promise<DirProposalFullType | null> => {
    try {
        const response = await axios(`/document/${documentId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createDirDocument = async (payload: ProposalSaveDataType): Promise<number | false> => {
    try {
        const response = await axios.post('/document/new', payload);
        return response?.data?.documentId || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveDirDocument = async (documentId: number | string, payload: ProposalSaveDataType): Promise<boolean> => {
    try {
        const response = await axios.post(`/document/${documentId}`, payload);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteDirDocument = async (documentId: number | string): Promise<boolean> => {
    try {
        const response = await axios.delete(`/document/${documentId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getObjectDocumentGenerations = async (
    objectId: number | string,
): Promise<ObjectProposalGenerationType[] | null> => {
    try {
        const response = await axios(`/document/object/${objectId}/generations`);
        return response?.data?.rows || [];
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createObjectDocumentGeneration = async (
    objectId: number | string,
    payload: ObjectProposalGenerationCreateDataType,
): Promise<{ stat: boolean; generationId: number } | null> => {
    try {
        const response = await axios.post(`/document/object/${objectId}/generations`, payload);
        return {
            stat: Boolean(response?.data?.stat),
            generationId: Number(response?.data?.generationId || 0),
        };
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const setObjectDocumentGenerationAccept = async (
    objectId: number | string,
    generationId: number | string,
    proposavAccept: boolean,
): Promise<boolean> => {
    try {
        const response = await axios.post(`/document/object/${objectId}/generations/${generationId}/accept`, {
            proposavAccept,
        });
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteObjectDocumentGeneration = async (
    objectId: number | string,
    generationId: number | string,
): Promise<boolean> => {
    try {
        const response = await axios.delete(`/document/object/${objectId}/generations/${generationId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const sendObjectDocumentEmail = async (
    objectId: number | string,
    payload: {
        email: string;
        file: LeadCloudFileType;
        proposalName?: string;
        objectName?: string;
        proposalType?: DocumentDirectoryType | string;
        mailContent: { subject: string; html: string };
    },
): Promise<boolean> => {
    try {
        const response = await axios.post(`/document/object/${objectId}/send-email`, payload);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const sendObjectDocumentGenerationEmail = async (
    objectId: number | string,
    generationId: number | string,
    payload: {
        email: string;
        emailBody?: string;
        mailContent: { subject: string; html: string };
    },
): Promise<boolean> => {
    try {
        const response = await axios.post(
            `/document/object/${objectId}/generations/${generationId}/send-email`,
            payload,
        );
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export interface ObjectDocumentPaymentCreatePayload {
    paymentAmount: number;
    description?: string;
    attach?: string | string[] | null;
}

export interface ObjectPaySummaryType {
    debtUsd: number;
    debtUah: number;
    paidUsd: number;
    paidUah: number;
}

export const getObjectPaySummary = async (
    objectId: number | string,
): Promise<ObjectPaySummaryType | null> => {
    try {
        const response = await axios(`/document/object/${objectId}/pay-summary`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getObjectDocumentPayments = async (
    objectId: number | string,
    generationId: number | string,
): Promise<{ payments: Record<string, any>[]; generation: Record<string, any> } | null> => {
    try {
        const response = await axios(`/document/object/${objectId}/generations/${generationId}/payments`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
}; 

export const addObjectDocumentPayment = async (
    objectId: number | string,
    generationId: number | string,
    payload: ObjectDocumentPaymentCreatePayload,
): Promise<{ stat: boolean; paymentId: number } | null> => {
    try {
        const response = await axios.post(
            `/document/object/${objectId}/generations/${generationId}/payments`,
            payload,
        );
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getObjectDocumentEmailHistory = async (objectId: number, generationId: number, source: 'object' | 'lead'): Promise<DocumentEmailHistoryType[] | null> => {
    try {
        const response = await axios.get(`/document/object/${objectId}/generations/${generationId}/email-history`, { params: { source } });
        return response.data.rows;
    } catch (error) {
        console.error(error);
        return null;
    }
};
